import { useQuery } from '@tanstack/react-query'
import { UNIFIED_DOORS_SUMMARY_KEY } from 'constants/tableQueryKeys'
import { useMemo } from 'react'
import { getDoorReportsSummary, listDoorReports } from 'services/reports'

/**
 * Restaurant doors (DoorsTableComponentV2) data source — reads the CRM-reports
 * read-model (`reports/doors/` + `reports/doors/summary/`) via the generated
 * reports client (`listDoorReports` / `getDoorReportsSummary`).
 *
 * The door analog of `useUnifiedChainsTable`. The restaurant-doors table is the
 * one door table that does NOT use `DECommonTable` — it has a bespoke component
 * (`DoorsTableComponentV2`) with elastic name/menu search, a product selector,
 * a rich LTV summary header (`total_ltv`, `places_count`), and a wide column set
 * (`DoorsTableColumnsV2`). So instead of the generic `useUnifiedDoorsTableActions`
 * hook, this returns the pieces the bespoke component needs:
 *
 *  - `listAction`: a bound list fetcher with a
 *    `(params, signal) => Promise<{ results }>` shape. The `reports/doors/`
 *    endpoint already returns rows in the shape the doors columns, selection, and
 *    row actions expect (nested `restaurant`, `restaurant.firstbite_id`, a
 *    top-level `door_proxy_id`, and `chain_proxy.id`), so the rows are returned
 *    as-is.
 *  - `unifiedCount`: the reports door count for the current filters, to drive
 *    pagination + `setTotalRowsInBackend` so paging matches the rows.
 *
 * (The `unified*` names on the return object are kept for a stable seam with the
 * consuming component; the data now comes from the reports read-model.)
 */

/**
 * Fetch one page of restaurant doors from the `reports/doors/` endpoint. The
 * reports rows already carry the shape the restaurant-doors consumers read
 * (door detail nested under `restaurant`, `restaurant.firstbite_id`, a top-level
 * `door_proxy_id`), so they are returned unchanged. Shared by
 * `useUnifiedDoorsReportsTable` (the doors target table) and `MobileDoorsListPage`.
 *
 * Typed `results: any[]` (not the generated `DoorReportRow[]`) on purpose:
 * `DoorsTableComponentV2`/`MobileDoorsListPage` still consume rows through the
 * legacy `DoorProxyReport` shape in places, which isn't structurally identical
 * to `DoorReportRow` (e.g. `chain_proxy.chain` nullability) — reconciling that
 * is part of the separate, in-flight Unified Data Tables effort, not this
 * client-migration.
 */
export async function fetchUnifiedRestaurantDoors(
  params: Record<string, any>,
  signal?: AbortSignal
): Promise<{ results: any[] }> {
  const res = await listDoorReports(params, { signal })
  return { results: res.results }
}

export function useUnifiedDoorsReportsTable(
  filterParams: Record<string, any>,
  // Gate for the summary query (doors tab active or a chain filter present).
  // Prevents the summary from firing/forwarding stats for a tab the user
  // isn't viewing.
  summaryEnabled = true
): {
  listAction: (
    params: Record<string, any>,
    signal?: AbortSignal
  ) => Promise<{ results: any[] }>
  unifiedCount: number | undefined
  unifiedChainsCount: number | undefined
  unifiedLtv: number | undefined
  unifiedSummaryFetching: boolean
} {
  const listAction = useMemo(() => {
    return (params: Record<string, any>, signal?: AbortSignal) =>
      fetchUnifiedRestaurantDoors(params, signal)
  }, [])

  const { data: doorsSummary, isFetching: unifiedSummaryFetching } = useQuery({
    // Use the default (finite, 0) staleTime so the count refetches after
    // mutations that invalidate UNIFIED_DOORS_SUMMARY_KEY. Do NOT set
    // `staleTime: Infinity` here: an infinite staleTime would leave the
    // count stale until a hard reload.
    enabled: summaryEnabled,
    queryKey: [UNIFIED_DOORS_SUMMARY_KEY, filterParams],
    queryFn: async ({ signal }) => {
      const res = await getDoorReportsSummary(filterParams, { signal })
      return (
        res?.results?.[0] ?? {
          places_count: 0,
          chains_count: 0,
          total_ltv: 0,
        }
      )
    },
  })

  return {
    listAction,
    // The full stat-card set for the filtered doors, all from the reports doors
    // summary. See DoorsTableComponentV2.
    unifiedCount: doorsSummary?.places_count,
    unifiedChainsCount: doorsSummary?.chains_count,
    // `total_ltv` is nullable in the aggregate response (a filter matching
    // zero rows returns a NULL sum); normalize null -> undefined the same way
    // `useUnifiedChainsTable` does. The `res?.results?.[0] ?? {...0 defaults}`
    // fallback above only covers a fully-missing row, not a present row with
    // a null `total_ltv`.
    unifiedLtv: doorsSummary?.total_ltv ?? undefined,
    unifiedSummaryFetching,
  }
}
